/* eslint-disable max-lines */
import {
 useContext, useEffect, useState
} from 'react';
import {
 useParams, useNavigate
} from 'react-router-dom';
import {
 Context
} from 'shared/context/GlobalState';
import {
 Button
} from 'shared/components/ui/button';
import {
 ResizablePanelGroup, ResizablePanel, ResizableHandle
} from 'shared/components/ui/resizable';
import {
 Skeleton
} from 'shared/components/ui/skeleton';
import {
 ArrowLeft, RotateCcw, Save, Code, X
} from 'lucide-react';
import {
 jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
 sanitizeOpcodeForCreate, sanitizeOpcodeForUpdate
} from 'builders/builderOpcode/jsonMode/templates';
import {
    OpcodeJsonShape, JsonOpcodeStep, FlowDefinition, StepKind,
    TypedInput, TypedOutput, AuditBlock, ResourceManifest, EngineCapabilities
} from 'builders/builderOpcode/jsonMode/types';
import {
 V2OpCodeBuilderProvider
} from './V2OpCodeBuilderContext';
import v2Handler from './V2OpCodeBuilderHandler';
import {
 buildV2Step, changeStepKind
} from './V2StepBuilder';
import V2NavigationPanel, {
 type SectionId
} from './V2NavigationPanel';
import V2DetailPanel from './V2DetailPanel';
import {
    PanelErrorBoundary
} from 'shared/components/ErrorBoundary';

const V2OpCodeBuilderPageInner = () => {
    const {
 opcodeId, oragID, subOragID
} = useParams();
    const navigate = useNavigate();
    const isUpdateMode = !!opcodeId;

    // Form state — V2+ shape
    const [opCodeName, setOpCodeName] = useState('');
    const [opCodeDescription, setOpCodeDescription] = useState('');
    const [inputs, setInputs] = useState<TypedInput[]>([]);
    const [outputs, setOutputs] = useState<TypedOutput[]>([]);
    const [defaultStepTimeoutMs, setDefaultStepTimeoutMs] = useState<number | undefined>(undefined);
    const [steps, setSteps] = useState<JsonOpcodeStep[]>([]);
    const [definitions, setDefinitions] = useState<JsonOpcodeStep[]>([]);
    const [flows, setFlows] = useState<FlowDefinition[]>([]);

    // Informational metadata (read-only, server-managed)
    const [serverOpcodeId, setServerOpcodeId] = useState<string | undefined>(undefined);
    const [schemaVersion, setSchemaVersion] = useState<string | undefined>(undefined);
    const [audit, setAudit] = useState<AuditBlock | undefined>(undefined);
    const [resourcesUsed, setResourcesUsed] = useState<ResourceManifest | undefined>(undefined);
    const [engineCapabilities, setEngineCapabilities] = useState<EngineCapabilities | undefined>(undefined);

    // UI state
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(isUpdateMode);
    const [selectedSection, setSelectedSection] = useState<SectionId>('config');
    const [showJsonPreview, setShowJsonPreview] = useState(false);

    const {
 addNewNotifcation
} = useContext(Context);

    // Fetch opcode data for update mode
    useEffect(() => {
        if (!isUpdateMode || !opcodeId) return;

        const fetchDetails = async () => {
            try {
                setIsLoading(true);
                const res = await jsonOpcodeService.getJSONOpcodeById({
                    organizationId: oragID,
                    subOrganizationId: subOragID,
                    opcodeId,
                });

                const data = res.data as any;
                if (data) {
                    // V2+ canonical: title; legacy: name. Prefer title.
                    setOpCodeName(data.title || data.name || '');
                    setOpCodeDescription(data.description || '');

                    // Inputs — accept either TypedInput[] or legacy string[].
                    const rawInputs = Array.isArray(data.inputs) ? data.inputs : [];
                    const typedInputs: TypedInput[] = rawInputs.map((x: any) => {
                        if (typeof x === 'string') {
                            return {
 name: x,
type: 'string',
required: true,
description: ''
};
                        }
                        return {
                            name: x?.name || '',
                            type: x?.type || 'string',
                            required: x?.required !== false,
                            description: x?.description || '',
                            ...(x?.default !== undefined ? {
 default: x.default
} : {}),
                        };
                    }).filter((i: TypedInput) => i.name);
                    setInputs(typedInputs);

                    // Outputs — V2+ TypedOutput[]; if legacy `output: {}` map, flatten.
                    if (Array.isArray(data.outputs)) {
                        setOutputs(data.outputs.map((o: any) => ({
                            name: o?.name || '',
                            type: o?.type || 'string',
                            description: o?.description || '',
                            oel_expression: o?.oel_expression || '',
                            ...(o?.schema ? {
 schema: o.schema
} : {}),
                        })).filter((o: TypedOutput) => o.name));
                    } else if (data.output && typeof data.output === 'object') {
                        const flat: TypedOutput[] = Object.entries(data.output)
                            .filter(([, v]) => typeof v === 'string')
                            .map(([k, v]) => ({
                                name: k,
                                type: 'string',
                                description: '',
                                oel_expression: v as string,
                            }));
                        setOutputs(flat);
                    } else {
                        setOutputs([]);
                    }

                    setSteps(data.steps || []);
                    setDefinitions(data.definitions || []);
                    setFlows(data.flows || []);
                    setDefaultStepTimeoutMs(
                        typeof data.default_step_timeout_ms === 'number' ? data.default_step_timeout_ms : undefined
                    );

                    // Informational fields (server-managed; surface in Metadata panel).
                    setServerOpcodeId(data.opcode_id);
                    setSchemaVersion(data.schema_version);
                    setAudit(data.audit);
                    setResourcesUsed(data.resources_used);
                    setEngineCapabilities(data.engine_capabilities_required);
                }
            } catch (error) {
                console.error('Error fetching opcode:', error);
                addNewNotifcation('Error fetching opcode details.', 'danger');
            } finally {
                setIsLoading(false);
            }
        };

        fetchDetails();
    }, [opcodeId]);

    // --- Step management handlers ---
    const handleAddStep = () => {
        const newStep = buildV2Step('llm', steps);
        const newSteps = [...steps, newStep];
        setSteps(newSteps);
        setSelectedSection(`step-${newSteps.length - 1}`);
    };

    const handleRemoveStep = (index: number) => {
        const remaining = steps.filter((_, i) => i !== index);
        const renumbered = remaining.map((step, i) => ({
            ...step,
            id: `step_${i + 1}`,
        }));
        setSteps(renumbered);

        // Adjust selected section
        if (selectedSection === `step-${index}`) {
            if (renumbered.length > 0) {
                const newIndex = Math.min(index, renumbered.length - 1);
                setSelectedSection(`step-${newIndex}`);
            } else {
                setSelectedSection('config');
            }
        } else if (selectedSection.startsWith('step-')) {
            const currentIdx = parseInt(selectedSection.replace('step-', ''), 10);
            if (currentIdx > index) {
                setSelectedSection(`step-${currentIdx - 1}`);
            }
        }
    };

    const handleStepKindChange = (index: number, kind: StepKind) => {
        const existing = steps[index];
        const newStep = changeStepKind(existing, kind, steps);
        setSteps(steps.map((s, i) => i === index ? newStep : s));
    };

    const handleStepChange = (index: number, updatedStep: JsonOpcodeStep) => {
        setSteps(steps.map((s, i) => i === index ? updatedStep : s));
    };

    const handleMoveStep = (index: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= steps.length) return;
        const newSteps = [...steps];
        [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
        setSteps(newSteps);

        // Follow the moved step
        if (selectedSection === `step-${index}`) {
            setSelectedSection(`step-${newIndex}`);
        } else if (selectedSection === `step-${newIndex}`) {
            setSelectedSection(`step-${index}`);
        }
    };

    // --- Save handler ---
    const handleSave = async () => {
        const opcode: OpcodeJsonShape = {
            // V2+ shape — wire normalizer accepts this verbatim.
            title: opCodeName.trim(),
            description: opCodeDescription,
            inputs,
            outputs,
            steps,
            definitions: definitions.length > 0 ? definitions : undefined,
            flows: flows.length > 0 ? flows : undefined,
            ...(typeof defaultStepTimeoutMs === 'number'
                ? {
 default_step_timeout_ms: defaultStepTimeoutMs
}
                : {}),
        };

        const validation = v2Handler.validateOpcode(opcode);
        if (!validation.isValid) {
            addNewNotifcation(validation.message, 'warning');
            return;
        }

        try {
            setIsSaving(true);

            if (isUpdateMode) {
                const sanitized = sanitizeOpcodeForUpdate(opcode);
                await jsonOpcodeService.updateJSONOpcode({
                    organizationId: oragID,
                    subOrganizationId: subOragID,
                    opcodeId: opcodeId!,
                    opcode: sanitized,
                });
                addNewNotifcation('Opcode updated successfully.', 'success');
            } else {
                const sanitized = sanitizeOpcodeForCreate(opcode);
                await jsonOpcodeService.createJSONOpcode({
                    organizationId: oragID,
                    subOrganizationId: subOragID,
                    opcode: sanitized,
                });
                addNewNotifcation('Opcode created successfully.', 'success');
            }

            navigateBack();
        } catch (error) {
            console.error('Error saving opcode:', error);
            if ((error as any)?.response?.status === 409) {
                addNewNotifcation('Opcode already exists. Please choose another name.', 'warning');
            } else {
                addNewNotifcation(
                    `Opcode cannot be ${isUpdateMode ? 'updated' : 'created'}. Please try again.`,
                    'danger'
                );
            }
        } finally {
            setIsSaving(false);
        }
    };

    const resetForm = () => {
        setOpCodeName('');
        setOpCodeDescription('');
        setInputs([]);
        setOutputs([]);
        setDefaultStepTimeoutMs(undefined);
        setSteps([]);
        setDefinitions([]);
        setFlows([]);
        setSelectedSection('config');
        addNewNotifcation('Form has been reset.', 'info');
    };

    const navigateBack = () => {
        navigate(`/organizations/${oragID}/sub/${subOragID}/op-codes`);
    };

    const isDisabled = !opCodeName.trim();

    const buildOpcodeJson = (): OpcodeJsonShape => ({
        title: opCodeName.trim(),
        description: opCodeDescription,
        inputs,
        outputs,
        steps,
        ...(definitions.length > 0 ? {
 definitions
} : {}),
        ...(flows.length > 0 ? {
 flows
} : {}),
        ...(typeof defaultStepTimeoutMs === 'number'
            ? {
 default_step_timeout_ms: defaultStepTimeoutMs
}
            : {}),
        ...(serverOpcodeId ? {
 opcode_id: serverOpcodeId
} : {}),
        ...(schemaVersion ? {
 schema_version: schemaVersion
} : {}),
        ...(audit ? {
 audit
} : {}),
        ...(resourcesUsed ? {
 resources_used: resourcesUsed
} : {}),
        ...(engineCapabilities ? {
 engine_capabilities_required: engineCapabilities
} : {}),
    });

    return (
        <div className="flex flex-col h-[calc(100vh-120px)] w-full">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b bg-card shrink-0">
                <Button variant="ghost" size="icon" onClick={navigateBack}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>

                <div className="flex-1 min-w-0">
                    {isLoading ? (
                        <Skeleton className="h-6 w-48" />
                    ) : (
                        <h1 className="text-lg font-semibold truncate">
                            {isUpdateMode ? 'Edit Opcode' : 'Create New V2 Opcode'}
                            {opCodeName && (
                                <span className="text-muted-foreground font-normal"> — {opCodeName}</span>
                            )}
                        </h1>
                    )}
                </div>

                {!isLoading && (
                    <div className="flex items-center gap-2 shrink-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowJsonPreview(!showJsonPreview)}
                            className={showJsonPreview ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600 hover:text-white' : ''}
                        >
                            <Code className="h-3.5 w-3.5 mr-1.5" />
                            JSON
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={resetForm}
                        >
                            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                            Reset
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={isSaving || isDisabled}
                        >
                            <Save className="h-3.5 w-3.5 mr-1.5" />
                            {isSaving
                                ? (isUpdateMode ? 'Updating...' : 'Creating...')
                                : (isUpdateMode ? 'Update Opcode' : 'Create Opcode')
                            }
                        </Button>
                    </div>
                )}
            </div>

            {/* Split Panel */}
            {isLoading ? (
                <div className="flex flex-1 border-t">
                    {/* Skeleton left panel */}
                    <div className="w-[25%] border-r bg-background p-3 space-y-3">
                        <Skeleton className="h-12 w-full rounded-md" />
                        <div className="pt-2">
                            <Skeleton className="h-4 w-16 mb-3" />
                            <div className="space-y-2">
                                <Skeleton className="h-8 w-full rounded-md" />
                                <Skeleton className="h-8 w-full rounded-md" />
                                <Skeleton className="h-8 w-full rounded-md" />
                            </div>
                        </div>
                        <Skeleton className="h-px w-full" />
                        <Skeleton className="h-8 w-full rounded-md" />
                        <Skeleton className="h-8 w-full rounded-md" />
                        <Skeleton className="h-px w-full" />
                        <Skeleton className="h-8 w-full rounded-md" />
                    </div>
                    {/* Skeleton right panel */}
                    <div className="flex-1 bg-card p-6 space-y-5">
                        <Skeleton className="h-7 w-52" />
                        <div className="space-y-4 max-w-4xl">
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-28" />
                                <Skeleton className="h-10 w-full rounded-md" />
                            </div>
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-36" />
                                <Skeleton className="h-24 w-full rounded-md" />
                            </div>
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-20" />
                                <Skeleton className="h-10 w-full rounded-md" />
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <ResizablePanelGroup direction="horizontal" className="flex-1 border-t">
                    <ResizablePanel defaultSize={25} minSize={18} maxSize={35} className="bg-background border-r">
                        <PanelErrorBoundary
                            name="opcode-v2-navigation"
                            title="The opcode outline could not be displayed"
                            recordType="opcode"
                            recordId={serverOpcodeId}
                            recordLabel={opCodeName}
                        >
                        <V2NavigationPanel
                        selectedSection={selectedSection}
                        onSelectSection={setSelectedSection}
                        opCodeName={opCodeName}
                        inputs={inputs}
                        steps={steps}
                        definitions={definitions}
                        flows={flows}
                        outputs={outputs}
                        onAddStep={handleAddStep}
                        onRemoveStep={handleRemoveStep}
                        onMoveStep={handleMoveStep}
                    />
                        </PanelErrorBoundary>
                    </ResizablePanel>

                    <ResizableHandle withHandle className="" />

                    <ResizablePanel defaultSize={showJsonPreview ? 45 : 75} className="bg-card">
                        {/* Tier 3 — the detail panel renders the stored opcode's
                            steps and definitions. A malformed step used to blank the
                            platform; now the outline panel beside it stays usable so
                            the step can be selected and deleted. */}
                        <PanelErrorBoundary
                            name="opcode-v2-detail"
                            title="This part of the opcode could not be displayed"
                            recordType="opcode"
                            recordId={serverOpcodeId}
                            recordLabel={opCodeName}
                        >
                        <V2DetailPanel
                        selectedSection={selectedSection}
                        opCodeName={opCodeName}
                        setOpCodeName={setOpCodeName}
                        opCodeDescription={opCodeDescription}
                        setOpCodeDescription={setOpCodeDescription}
                        inputs={inputs}
                        setInputs={setInputs}
                        defaultStepTimeoutMs={defaultStepTimeoutMs}
                        setDefaultStepTimeoutMs={setDefaultStepTimeoutMs}
                        steps={steps}
                        onStepKindChange={handleStepKindChange}
                        onStepChange={handleStepChange}
                        onMoveStep={handleMoveStep}
                        definitions={definitions}
                        setDefinitions={setDefinitions}
                        flows={flows}
                        setFlows={setFlows}
                        outputs={outputs}
                        setOutputs={setOutputs}
                        opcodeId={serverOpcodeId}
                        schemaVersion={schemaVersion}
                        audit={audit}
                        resourcesUsed={resourcesUsed}
                        engineCapabilities={engineCapabilities}
                    />
                        </PanelErrorBoundary>
                    </ResizablePanel>

                    {showJsonPreview && (
                    <>
                        <ResizableHandle withHandle className="" />
                        <ResizablePanel defaultSize={30} minSize={20} className="bg-background">
                            <div className="h-full flex flex-col">
                                <div className="flex items-center justify-between px-4 py-2 border-b">
                                    <span className="text-sm font-semibold">JSON Preview</span>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={() => setShowJsonPreview(false)}
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-4">
                                    <pre className="text-xs font-mono whitespace-pre-wrap break-words text-foreground">
                                        {JSON.stringify(buildOpcodeJson(), null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </ResizablePanel>
                    </>
                )}
                </ResizablePanelGroup>
            )}

            {/* Summary bar */}
            <div className="flex items-center gap-4 px-4 py-2 border-t bg-card text-xs text-muted-foreground shrink-0">
                <span>{inputs.length} input{inputs.length !== 1 ? 's' : ''}</span>
                <span className="text-border">|</span>
                <span>{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
                <span className="text-border">|</span>
                <span>{definitions.length} definition{definitions.length !== 1 ? 's' : ''}</span>
                <span className="text-border">|</span>
                <span>{flows.length} flow{flows.length !== 1 ? 's' : ''}</span>
                <span className="text-border">|</span>
                <span>{outputs.length} output{outputs.length !== 1 ? 's' : ''}</span>
            </div>
        </div>
    );
};

// Wrapper that provides the V2 context
const V2OpCodeBuilderPage = () => {
    const {
 oragID, subOragID
} = useParams();

    return (
        <V2OpCodeBuilderProvider organizationId={oragID} subOrganizationId={subOragID}>
            <V2OpCodeBuilderPageInner />
        </V2OpCodeBuilderProvider>
    );
};

export default V2OpCodeBuilderPage;
